// The account's role, usage and limits (PROTOCOL.md §9.1, §9.3), quota refusals as the relay reports them, sign-up with
// an invite, and deleting the account (§9.6). No DOM: the passkey ceremony is the caller's (`assert`/`create` below are
// the browser's or a native client's).
import { ApiError, type Api } from './api.ts';

export interface Usage {
  /** Stored log bytes across the account's projects (inline entries and blobs). */
  bytes: number;
  projects: number;
  /** Live reader links. */
  readerLinks: number;
  /** Live agent grants. */
  agentGrants: number;
}

export type Limits = Usage;

export interface AccountStanding {
  role: 'owner' | 'writer';
  usage: Usage;
  /** Null for the owner, who is unlimited. */
  limits: Limits | null;
}

/** Role, usage and limits from `GET /api/account` (the rest of its reply is the account page's). */
export async function accountStanding(api: Api): Promise<AccountStanding> {
  const a = await api.get<AccountStanding>('/api/account');
  return { role: a.role, usage: a.usage, limits: a.limits };
}

/** How full each limit is (0–1, or null when unlimited). */
export function usageFractions(s: AccountStanding): Record<keyof Usage, number | null> {
  const f = (k: keyof Usage) => (s.limits ? (s.limits[k] > 0 ? s.usage[k] / s.limits[k] : 1) : null);
  return { bytes: f('bytes'), projects: f('projects'), readerLinks: f('readerLinks'), agentGrants: f('agentGrants') };
}

/** A quota refusal (`code: "quota"`) from an API error or a sync error frame: the limit and the usage before the write. */
export interface QuotaError {
  limit: number;
  used: number;
}

/** The quota details of a sync error frame (`{t:"error", code:"quota", limit, used}`), else null. */
export function quotaOfFrame(header: { t?: string; code?: string; limit?: unknown; used?: unknown }): QuotaError | null {
  if (header.t !== 'error' || header.code !== 'quota') return null;
  return { limit: Number(header.limit ?? 0), used: Number(header.used ?? 0) };
}

/** Whether an API call failed on a quota (`403 quota`, or `413 quota` for a blob upload). */
export const isQuotaError = (e: unknown): e is ApiError => e instanceof ApiError && e.code === 'quota';

/** The limit and usage of an API quota refusal, else null. */
export const quotaOfError = (e: unknown): QuotaError | null =>
  isQuotaError(e) ? { limit: Number(e.details.limit ?? 0), used: Number(e.details.used ?? 0) } : null;

/** Whether an API call failed because the account is suspended, being deleted or deleted (§9.4, §9.6). */
export const accountGone = (e: unknown): 'suspended' | 'deleting' | 'deleted' | null =>
  e instanceof ApiError && e.status === 401 && (e.code === 'suspended' || e.code === 'deleting' || e.code === 'deleted') ? e.code : null;

// Sign-up with an invite ------------------------------------------------------------------------------------------------

export interface SignupOptions {
  challengeId: string;
  accountId: string;
  options: Record<string, unknown>;
}

/** `POST /api/signup/options` with an invite (null on an empty relay, or where sign-up is open in development). */
export const signupOptions = (api: Api, name: string, device: { name: string; kind: string }, invite: string | null) =>
  api.call<SignupOptions>('POST', '/api/signup/options', { name, device, ...(invite ? { invite } : {}) }, false);

// Deleting the account ----------------------------------------------------------------------------------------------------

/**
 * Deletes the account after a fresh passkey assertion: `assert` runs the WebAuthn ceremony with the options the relay
 * gives and returns the response JSON; `confirm` must be the account's name. Everything on the relay goes (§9.6); the
 * tokens are forgotten here.
 */
export async function deleteAccount(api: Api, confirm: string, assert: (options: Record<string, unknown>) => Promise<unknown>): Promise<void> {
  const { challengeId, options } = await api.call<{ challengeId: string; options: Record<string, unknown> }>('POST', '/api/account/delete/options', {});
  const response = await assert(options);
  await api.call('POST', '/api/account/delete', { challengeId, response, confirm });
  api.forget();
}
